import * as Sentry from '@sentry/tanstackstart-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
} from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCallStore } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { IS_IOS_AUDIO_ROUTED } from '#features/media-window/audioRouting';
import { microphoneSwitchConfirmed, readActiveMicrophone } from '#features/media/activeMicrophone';
import { saveDevicePreference } from '#features/media/devicePreference';
import { applyPhoneSpeakerDefault } from '#features/media/phoneSpeakerDefault';
import { createMediaErrorReporter } from '#features/media/reportMediaError';
import { preferredRoomDevices } from '#features/media/roomDeviceDefaults';
import { MIC_SPEECH_THRESHOLD, useMicLevel } from '#features/media/useMicLevel';
import { requestBluetoothAccess } from '#features/notifications/utils/deviceSettings';
import { useQualityReporter } from '#features/quality';
import { useNativeVoiceRoom } from '#features/voice/hooks/useNativeVoiceRoom';
import { leaveVoice } from '#features/voice/model/voice';
import { takeVoiceRoomSwitch } from '#features/voice/model/voiceRoomSwitch';
import {
  useVoiceStore,
  type VoiceMember,
  type VoiceQuality,
} from '#features/voice/model/voiceStore';
import { resolveDisconnectOutcome } from '#features/voice/utils/disconnectOutcome';
import {
  completeNativeVoiceMic,
  onNativeVoiceMicRequest,
} from '#features/voice/utils/nativeVoiceLiveActivity';
import { useNativeVoiceSession } from '#features/voice/utils/nativeVoiceSession';
import { playJoinSound } from '#features/voice/utils/roomSounds';
import { envConfig } from '#constants/envConfig';

/** Три деления вместо пяти: см. комментарий у `VoiceQuality`. */
const toVoiceQuality = (quality: ConnectionQuality): VoiceQuality => {
  if (quality === ConnectionQuality.Lost) {
    return 'lost';
  }
  if (quality === ConnectionQuality.Poor) {
    return 'unstable';
  }
  if (quality === ConnectionQuality.Unknown) {
    return 'unknown';
  }
  return 'good';
};

/**
 * Человек, а не соединение. LiveKit-identity выглядит как `<userId>#<хвост устройства>`: хвост
 * рождается при выдаче токена, чтобы вкладки и телефон одного аккаунта не выбивали друг друга.
 * Побочный эффект — на каждое соединение приходит отдельный участник, и комната начинает считать
 * одного человека дважды.
 */
const userIdOf = (identity: string) => identity.split('#')[0] || identity;

/** Имя и аватар лежат в токене: `name` и JSON-метаданные участника. */
const readProfile = (participant: { name?: string; metadata?: string }) => {
  let avatar: string | null = null;
  if (participant.metadata) {
    try {
      avatar = (JSON.parse(participant.metadata) as { avatar?: string }).avatar ?? null;
    } catch {
      avatar = null;
    }
  }
  // Аватар в токене — тот же относительный путь, что и в WS-событиях, поэтому и приводится тем же
  // хелпером: дефолтная картинка превращается в null, и Avatar рисует инициалы.
  return { displayName: participant.name || null, avatar: toCallAvatarUrl(avatar) };
};

/**
 * Комната у человека одна, а хук могут вызвать в нескольких местах (виджет, карточка сессии).
 * Владельцем соединения становится первый смонтировавшийся — остальные получают то же состояние
 * из стора, но второго подключения с тем же токеном не создают.
 */
let roomOwner: symbol | null = null;

/**
 * Владеет комнатой LiveKit для группового голоса сессии. Медиа-часть (SDP, ICE, TURN, автоматические
 * переподключения) целиком на SDK — здесь только подключение, микрофон, живое состояние участников
 * и корректный выход.
 *
 * Отличие от 1:1 звонка (`useCallRoom`) не в объёме, а в природе: там собеседник ровно один и его
 * исчезновение означает конец разговора, здесь состав меняется всё время и уход последнего человека
 * — обычное дело, комната живёт до конца сессии.
 *
 * Видео и демонстрации экрана в этой версии нет: сервер не выдаёт на них прав в токене, поэтому
 * подписываемся только на аудио.
 */
const useWebVoiceRoom = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const phase = useVoiceStore((s) => s.phase);
  const room = useVoiceStore((s) => s.room);
  const micEnabled = useVoiceStore((s) => s.micEnabled);

  const roomRef = useRef<Room | null>(null);
  const [microphoneTrack, setMicrophoneTrack] = useState<MediaStreamTrack | null>(null);
  const [activeDeviceIds, setActiveDeviceIds] = useState<
    Partial<Record<'audioinput' | 'audiooutput', string>>
  >({});
  const refreshDeviceIds = useCallback(() => {
    const live = roomRef.current;
    const capture =
      live?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
        ?.mediaStreamTrack ?? null;
    setMicrophoneTrack(capture);
    setActiveDeviceIds(
      live
        ? {
            audioinput:
              IS_IOS_AUDIO_ROUTED && !envConfig.IS_NATIVE
                ? readActiveMicrophone(capture)?.deviceId || undefined
                : live.getActiveDevice?.('audioinput'),
            audiooutput:
              live.getActiveDevice?.('audiooutput') ??
              live.options?.audioOutput?.deviceId ??
              'default',
          }
        : {}
    );
  }, []);
  const roomSessionRef = useRef<string | null>(null);
  const mediaReporterRef = useRef(createMediaErrorReporter());
  const reportMediaError = useCallback(
    (
      error: unknown,
      operation: string,
      activeRoom = roomRef.current,
      requestedEnabled?: boolean
    ) => {
      if (activeRoom !== roomRef.current) return;
      const track = activeRoom?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
        ?.mediaStreamTrack;
      mediaReporterRef.current(error, {
        scope: 'voiceRoom',
        operation,
        sessionId: useVoiceStore.getState().room?.sessionId,
        connectionState: activeRoom?.state,
        requestedEnabled,
        microphoneEnabled: activeRoom?.localParticipant.isMicrophoneEnabled,
        microphoneTrackState: track?.readyState,
        microphoneTrackMuted: track?.muted,
      });
    },
    []
  );
  const ownerIdRef = useRef<symbol>(Symbol('voice-room'));

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let off: (() => void) | undefined;
    void onNativeVoiceMicRequest(async (request) => {
      const live = roomRef.current;
      const state = useVoiceStore.getState();
      // The call room also listens. Only the owner may acknowledge this intent.
      if (
        !live ||
        roomSessionRef.current !== request.sessionId ||
        state.room?.sessionId !== request.sessionId
      )
        return;
      let success = false;
      if (
        live?.state === ConnectionState.Connected &&
        state.room?.sessionId === request.sessionId &&
        !state.micDenied
      ) {
        try {
          await live.localParticipant.setMicrophoneEnabled(!request.muted);
          success =
            active &&
            live === roomRef.current &&
            useVoiceStore.getState().room?.sessionId === request.sessionId;
        } catch (error) {
          Sentry.captureException(error, {
            tags: { scope: 'voiceRoom', operation: 'lock-screen-microphone' },
          });
        }
      }
      const accepted = await completeNativeVoiceMic(request.requestId, success).catch(() => false);
      if (
        accepted &&
        active &&
        live === roomRef.current &&
        useVoiceStore.getState().room?.sessionId === request.sessionId
      ) {
        useVoiceStore.getState().setMicEnabled(!request.muted);
      }
    })
      .then((remove) => {
        if (active) off = remove;
        else remove();
        return undefined;
      })
      .catch(() => {});
    return () => {
      active = false;
      off?.();
    };
  }, [enabled]);

  // Телеметрия связи: считается всегда, никого ни о чём не спрашивая.
  const quality = useQualityReporter('VOICE');
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  /** Элементы <audio> по идентификатору участника — по одному на человека, переиспользуются. */
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  /**
   * Браузер отказался воспроизводить звук. Человек при этом сидит в полной тишине при идеальной
   * сети и не понимает, почему его «не слышат» — на деле не слышит он. В звонках это ловилось с
   * самого начала, а в войсе нет: элементы создавались с `autoplay = true`, и отклонённый
   * `play()` пропадал молча. Отсюда и `playback_blocked = 0` по всем VOICE-сеансам в телеметрии —
   * нулём там считалось «не измеряем», а не «не бывает».
   */
  const [audioBlocked, setAudioBlocked] = useState(false);

  const micDenied = useVoiceStore((s) => s.micDenied);
  const setMicDenied = useVoiceStore((s) => s.setMicDenied);
  const setConnected = useVoiceStore((s) => s.setConnected);
  const setReconnecting = useVoiceStore((s) => s.setReconnecting);
  const updateLiveState = useVoiceStore((s) => s.updateLiveState);
  const setQuality = useVoiceStore((s) => s.setQuality);
  const reset = useVoiceStore((s) => s.reset);

  const micLevel = useMicLevel(
    enabled && IS_IOS_AUDIO_ROUTED && phase === 'connected' && micEnabled && !micDenied,
    undefined,
    microphoneTrack
  );
  const localSpeakingRef = useRef(false);
  const localSpeaking =
    IS_IOS_AUDIO_ROUTED &&
    phase === 'connected' &&
    micEnabled &&
    !micDenied &&
    micLevel.listening &&
    micLevel.level > MIC_SPEECH_THRESHOLD;
  localSpeakingRef.current = localSpeaking;

  /** Снимок живого состояния: кто подключён, кто говорит и кто выключил микрофон. */
  const pushLiveState = useCallback(
    (activeRoom: Room) => {
      const everyone = [
        activeRoom.localParticipant,
        ...activeRoom.remoteParticipants.values(),
      ].filter((participant) => Boolean(participant.identity));

      const rawMic =
        activeRoom.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
          ?.mediaStreamTrack ?? null;
      setMicrophoneTrack(rawMic);

      // Схлопываем по человеку: иначе тот, кто открыл вторую вкладку или зашёл ещё и с телефона,
      // занимает два места из пяти и стоит в списке дважды — проверено на проде-стенде 27.08,
      // счётчик показывал «3 of 5» на двоих живых людей.
      const connected = [...new Set(everyone.map((p) => userIdOf(p.identity)))];
      const speakers = activeRoom.activeSpeakers
        .filter((p) => !IS_IOS_AUDIO_ROUTED || !p.isLocal)
        .map((p) => userIdOf(p.identity));
      if (localSpeakingRef.current && activeRoom.localParticipant.isMicrophoneEnabled) {
        speakers.push(userIdOf(activeRoom.localParticipant.identity));
      }
      const speaking = [...new Set(speakers)];

      // Замьючен — только если молчат ВСЕ его соединения: с телефона человек может говорить,
      // а вкладка на компьютере при этом стоять выключенной.
      const talkingSomewhere = new Set(
        everyone.filter((p) => p.isMicrophoneEnabled).map((p) => userIdOf(p.identity))
      );
      const muted = connected.filter((userId) => !talkingSomewhere.has(userId));

      const profiles = Object.fromEntries(
        everyone.map((p) => [userIdOf(p.identity), readProfile(p)])
      );
      updateLiveState({ connected, speaking, muted, profiles });
    },
    [updateLiveState]
  );

  useEffect(() => {
    if (roomRef.current) pushLiveState(roomRef.current);
    if (localSpeaking) quality.tracker.onSpoke();
  }, [localSpeaking, pushLiveState, quality.tracker]);

  const attachAudio = useCallback((track: RemoteTrack, identity: string) => {
    if (track.kind !== Track.Kind.Audio) {
      return;
    }
    /*
      Своё же другое устройство не слушаем. Сервер даёт каждому входу свою identity
      (`<userId>#<хвост>`), поэтому телефон и браузер одного человека – два участника комнаты, и
      каждый проигрывал голос другого: человек слышал сам себя, даже выключив микрофон на этом
      устройстве (Firefox + телефон рядом, 02.10.2026).
    */
    const own = roomRef.current?.localParticipant.identity;
    if (own && sameUser(own, identity)) {
      return;
    }
    const container = audioContainerRef.current;
    if (!container) {
      return;
    }
    let element = audioElementsRef.current.get(identity);
    if (!element) {
      element = document.createElement('audio');
      element.autoplay = true;
      element.dataset.identity = identity;
      container.appendChild(element);
      audioElementsRef.current.set(identity, element);
    }
    // Заглушённый остаётся заглушённым и после переподписки: трек пересоздаётся при каждом
    // переподключении, и без этой строки человек, которого я выключил, снова начинает говорить.
    element.muted = useVoiceStore.getState().localMuted[identity] ?? false;
    track.attach(element);
  }, []);

  const detachAudio = useCallback((identity: string) => {
    const element = audioElementsRef.current.get(identity);
    if (element) {
      element.remove();
      audioElementsRef.current.delete(identity);
    }
  }, []);

  /**
   * ⚠ В зависимостях этого эффекта не должно быть `phase` — и вообще ничего, что меняется в ответ
   * на события самой комнаты. Пока `phase` там стоял, соединение пересоздавалось ровно в момент,
   * когда оно установилось: `ConnectionStateChanged(Connected)` прилетает ещё внутри `connect()`,
   * фаза уезжает в `connected`, эффект перезапускается, живая комната сносится и на её место
   * встаёт вторая. Отдельная фаза «idle» здесь не нужна: комната и фаза живут одной парой —
   * `setConnecting` ставит обе, `reset` обе снимает, — поэтому `room === null` и есть «idle».
   */
  useEffect(() => {
    if (!enabled || !room) {
      return undefined;
    }
    if (roomRef.current) {
      return undefined;
    }
    if (roomOwner && roomOwner !== ownerIdRef.current) {
      return undefined;
    }
    roomOwner = ownerIdRef.current;
    // Ссылаемся на СБОР, а не на комнату: комната у сбора ровно одна (unique session_id в
    // voice_room), а идентификатора комнаты у клиента нет вовсе.
    quality.setTarget(room.sessionId);

    let disposed = false;
    // Сигнал звучит один раз за заход, а не на каждое возвращение связи: переподключения бывают
    // частыми, и щёлканье на каждое превратило бы подтверждение в раздражитель.
    let joinAnnounced = false;
    // adaptiveStream и dynacast — про видео; в голосовой комнате они ничего не решают, но включённый
    // adaptiveStream в фоне душит подписки, а вкладка в фоне здесь норма: человек играет.
    // Устройства, выбранные в разделе проверки: иначе человек выбирает гарнитуру там, а говорит
    // здесь в микрофон вебкамеры.
    const livekitRoom = new Room({
      adaptiveStream: false,
      dynacast: false,
      ...preferredRoomDevices(),
    });
    roomRef.current = livekitRoom;
    roomSessionRef.current = room.sessionId;
    // Копия для очистки: к моменту её вызова ref уже может указывать на другую комнату.
    const audioElements = audioElementsRef.current;
    useVoiceStore.getState().setMicDenied(false);
    setAudioBlocked(false);

    livekitRoom
      .on(RoomEvent.ActiveDeviceChanged, () => {
        if (!disposed) refreshDeviceIds();
      })
      .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        attachAudio(track, participant.identity);
        pushLiveState(livekitRoom);
      })
      .on(RoomEvent.TrackUnsubscribed, (_track, _publication, participant) => {
        detachAudio(participant.identity);
        pushLiveState(livekitRoom);
      })
      .on(RoomEvent.ParticipantConnected, () => pushLiveState(livekitRoom))
      .on(RoomEvent.ParticipantDisconnected, (participant) => {
        detachAudio(participant.identity);
        pushLiveState(livekitRoom);
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        if (speakers.some((speaker) => speaker.isLocal)) {
          // «Просидел полчаса и не сказал ни слова» — признак мёртвого микрофона, которого
          // не видно ни по одной сетевой метрике.
          quality.tracker.onSpoke();
        }
        pushLiveState(livekitRoom);
      })
      .on(RoomEvent.LocalTrackPublished, () => pushLiveState(livekitRoom))
      .on(RoomEvent.LocalTrackUnpublished, () => pushLiveState(livekitRoom))
      .on(RoomEvent.TrackMuted, () => pushLiveState(livekitRoom))
      .on(RoomEvent.TrackUnmuted, () => pushLiveState(livekitRoom))
      // Качество показываем только своё: чужие деления человек всё равно не чинит, а «у Киры
      // плохая связь» звучит как обвинение.
      .on(RoomEvent.ConnectionQualityChanged, (connectionQuality, participant) => {
        if (participant.identity === livekitRoom.localParticipant.identity) {
          setQuality(toVoiceQuality(connectionQuality));
          // До сих пор деление только рисовалось иконкой и забывалось. Теперь ещё и копится:
          // «связь была плохой 4 минуты из 6» — это ответ, а иконка в моменте — нет.
          quality.tracker.onQuality(connectionQuality);
        }
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        quality.tracker.onConnectionState(state);
        if (state === ConnectionState.Connected) {
          setConnected();
          pushLiveState(livekitRoom);
          if (!joinAnnounced) {
            joinAnnounced = true;
            // Звук подтверждает факт, а не намерение: играем на состоявшемся подключении, а не
            // на нажатии кнопки. Отказ браузера дать звук ничего не ломает — см. playJoinSound.
            void playJoinSound();
          }
        }
        if (state === ConnectionState.Reconnecting) {
          setReconnecting();
        }
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (!livekitRoom.canPlaybackAudio) {
          // В телеметрию — иначе такой сеанс уезжает в отчёт здоровым: сеть идеальная, качество
          // «good», а человек не слышал ничего.
          quality.tracker.onPlaybackBlocked();
          setAudioBlocked(true);
          // Ждать случайного тапа мало — в звонках у человека в Safari блокировка повторялась
          // пять раз за разговор. Поэтому и кнопка в интерфейсе, и попытка на первом же касании.
          document.addEventListener(
            'pointerdown',
            () => {
              livekitRoom.startAudio().catch(() => undefined);
            },
            { once: true }
          );
        } else {
          setAudioBlocked(false);
        }
      })
      .on(RoomEvent.Disconnected, (reason) => {
        if (disposed) {
          // Комната уже разобрана, а её сводка отправлена: причина от мертвеца легла бы на
          // следующий сеанс того же человека.
          return;
        }
        quality.tracker.onDisconnected(reason);
        // Разрыв не по нашей инициативе (закрыли комнату, выгнали, сеть умерла насовсем).
        const leavingRoom = useVoiceStore.getState().room;
        const sessionId = leavingRoom?.sessionId ?? null;
        reset();
        switch (
          resolveDisconnectOutcome(
            reason,
            useCallStore.getState().phase,
            // Переход между комнатами: метку поставил сам вход, см. `voiceRoomSwitch`.
            takeVoiceRoomSwitch(sessionId)
          )
        ) {
          case 'call':
            // Делаем ровно то, что сделал бы арбитраж: комната – для «Вернуться в голос» на
            // плашке конца звонка, отметка «я был в голосе» снимается – звать обратно будет она.
            if (leavingRoom) {
              useVoiceStore.getState().rememberInterruptedRoom(leavingRoom);
            }
            useVoiceStore.getState().forgetVoiceSession();
            return;
          case 'otherTab':
            /*
              Голос забрало другое устройство. Отметку «я в голосе» снимаем: иначе после
              перезагрузки этого устройства автовозврат забрал бы голос обратно, и два устройства
              перетягивали бы его по очереди. Вернуть сюда можно кнопкой на карточке.
            */
            useVoiceStore.getState().forgetVoiceSession();
            useVoiceStore.getState().setError('voice.other.tab', sessionId);
            return;
          case 'silent':
            return;
          /*
            Хост выгнал. Говорим об этом сразу и словами про удаление, а не про обрыв: до этого
            человек читал «Потеряна связь» с кнопкой «Вернуться», жал её и только тогда узнавал
            правду. Отметку «я был в голосе» снимаем - звать обратно туда, откуда выгнали, ложь.
          */
          case 'removed':
            useVoiceStore.getState().forgetVoiceSession();
            useVoiceStore.getState().setError('voice.room.banned', sessionId);
            return;
          case 'lost':
            useVoiceStore.getState().setError('voice.connection.lost', sessionId);
        }
      });

    const connect = async () => {
      try {
        // Android 12+: без разрешения WebView не отдаёт звук в Bluetooth-наушники. До подключения,
        // пока звук ещё не заведён; отказ голос не ломает (13.10.2026).
        await requestBluetoothAccess();
        if (disposed) {
          return;
        }
        await livekitRoom.connect(room.livekitUrl, room.token);
        if (disposed) {
          return;
        }
        livekitRoom.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((publication) => {
            if (publication.track) {
              attachAudio(publication.track as RemoteTrack, participant.identity);
            }
          });
        });

        try {
          await livekitRoom.localParticipant.setMicrophoneEnabled(
            useVoiceStore.getState().micEnabled
          );
        } catch (error) {
          if (disposed) return;
          reportMediaError(
            error,
            'microphone-start',
            livekitRoom,
            useVoiceStore.getState().micEnabled
          );
          // Микрофон запрещён системой или браузером: в комнате остаёмся — слушать можно, — но
          // интерфейс обязан сказать об этом, иначе человек будет говорить в пустоту.
          useVoiceStore.getState().setMicDenied(true);
          // И в телеметрию: без этого отказ выглядит как обычное молчание, и по цифрам нельзя
          // отличить «людям не дают микрофон на этой платформе» от «люди слушали».
          quality.tracker.onMicDenied();
          useVoiceStore.getState().setMicEnabled(false);
        }
        // Без выбора звук шёл «у уха» – громкая связь по умолчанию (Android и iPhone).
        await applyPhoneSpeakerDefault(livekitRoom, (kind) => Room.getLocalDevices(kind, false));
        if (disposed) {
          return;
        }
        refreshDeviceIds();
        setConnected();
        pushLiveState(livekitRoom);
      } catch (error) {
        if (!disposed) {
          // Немой catch здесь означал, что неудачный вход в голос не виден нигде: карточка молча
          // возвращалась к кнопке «войти», в Sentry не было ни строки, а телеметрия такой заход
          // не описывает вовсе — сводка существует только у состоявшегося сеанса. Отличить
          // «медиасервер недоступен» от «сеть у человека» было нечем. В звонках это чинилось
          // ещё в августе (`useCallRoom`), здесь — тем же способом и с теми же тегами, чтобы
          // обе поверхности искались в Sentry одним запросом.
          Sentry.withScope((scope) => {
            scope.setTag('scope', 'voiceRoom');
            scope.setTag('type', 'livekit-connect');
            scope.setLevel('warning');
            scope.setContext('voice', {
              sessionId: room.sessionId,
              livekitUrl: room.livekitUrl,
              online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
              connectionState: livekitRoom.state,
            });
            Sentry.captureException(error);
          });
          reset();
          // И человеку: без этого карточка просто откатывалась к «войти», и он жал её снова.
          useVoiceStore.getState().setError('voice.connect.failed', room.sessionId);
        }
      }
    };

    void connect();

    return () => {
      disposed = true;
      // Сводка отправляется на КАЖДОМ выходе, а не при закрытии комнаты: комната живёт часами,
      // а человек за вечер заходит и выходит по нескольку раз. Сервер складывает заходы в одну
      // строку, см. ON CONFLICT в media_quality.
      //
      // Причину проставляем сами и ДО отправки: событие Disconnected прилетит уже после
      // disconnect(), то есть после снимка, и без этой строки любой выход по своей воле уезжал
      // бы в отчёт без причины вовсе. Настоящую причину она не затирает — первая побеждает.
      quality.tracker.onDisconnected(DisconnectReason.CLIENT_INITIATED);
      quality.flush();
      roomRef.current = null;
      roomSessionRef.current = null;
      setActiveDeviceIds({});
      setMicrophoneTrack(null);
      if (roomOwner === ownerIdRef.current) {
        roomOwner = null;
      }
      audioElements.forEach((element) => element.remove());
      audioElements.clear();
      void livekitRoom.disconnect();
    };
  }, [
    enabled,
    room,
    refreshDeviceIds,
    attachAudio,
    detachAudio,
    pushLiveState,
    setConnected,
    setReconnecting,
    setQuality,
    reset,
    quality,
    reportMediaError,
  ]);

  /**
   * Микрофон переключается в сторе, сюда прилетает уже как факт.
   *
   * Спрашиваем состояние у самой комнаты, а не у нашей фазы: публиковать микрофон в ещё не
   * подключённую комнату нельзя. Запрос уходит в закрытый сигнальный канал и молча пропадает,
   * публикация висит до таймаута SDK и всё это время держит источник занятым — а следующий, уже
   * правильный вызов дожидается той же повисшей публикации и падает вместе с ней. Итог: человек
   * сидит в комнате с зелёной кнопкой и мёртвым микрофоном.
   */
  useEffect(() => {
    const activeRoom = roomRef.current;
    if (!activeRoom || activeRoom.state !== ConnectionState.Connected) {
      return;
    }
    const apply = async () => {
      try {
        await activeRoom.localParticipant.setMicrophoneEnabled(micEnabled);
        // Снимаем пометку только с удавшегося ВКЛЮЧЕНИЯ: выключить заблокированный микрофон
        // получается всегда, и на этом предупреждение о блокировке исчезало бы само.
        if (micEnabled) {
          setMicDenied(false);
        }
      } catch (error) {
        if (activeRoom !== roomRef.current) return;
        reportMediaError(error, 'microphone-toggle', activeRoom, micEnabled);
        setMicDenied(true);
        quality.tracker.onMicDenied();
        // Кнопка обязана показывать факт, а не намерение. Пока она горит зелёным при мёртвом
        // микрофоне, человек узнаёт о том, что его не слышно, от собеседников.
        useVoiceStore.getState().setMicEnabled(false);
      }
    };
    void apply();
    // `phase` в зависимостях именно как триггер: эффект читает состояние у самой комнаты через
    // ref, но перезапуститься обязан в момент, когда она подключилась. `quality.tracker` и
    // `setMicDenied` — стабильные ссылки (useMemo и селектор zustand), лишних прогонов не дают.
  }, [micEnabled, phase, quality.tracker, setMicDenied, reportMediaError]);

  /**
   * Возвращение в приложение после похода за разрешением.
   *
   * <p>Без этого путь чинится, но обрывается на последнем шаге: человек читает «открой настройки»,
   * открывает, выдаёт микрофон, возвращается — и видит ровно тот же красный текст с тем же
   * советом. Проверено на устройстве 27.08: единственным выходом было нажать кнопку микрофона, о
   * чём интерфейс не говорил нигде.
   *
   * <p>Пробуем захват, а не спрашиваем разрешение: на Android ≤12 системная проверка отвечает
   * «выдано» и там, где его нет, поэтому верить ей нельзя ни в одну сторону — а фактическая
   * попытка отвечает честно. Не удалось — молча остаёмся как были, человек ничего не терял.
   */
  useEffect(() => {
    if (!micDenied || phase !== 'connected') {
      return undefined;
    }

    const retryAfterReturn = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const activeRoom = roomRef.current;
      if (!activeRoom || activeRoom.state !== ConnectionState.Connected) {
        return;
      }
      void (async () => {
        try {
          await activeRoom.localParticipant.setMicrophoneEnabled(true);
          const store = useVoiceStore.getState();
          store.setMicDenied(false);
          // Захват удался — значит разрешение вернули.
          store.setMicEnabled(true);
        } catch (error) {
          reportMediaError(error, 'microphone-recover', activeRoom, true);

          // Разрешения так и нет — оставляем всё как было, человек ничего не терял.
        }
      })();
    };

    document.addEventListener('visibilitychange', retryAfterReturn);
    return () => document.removeEventListener('visibilitychange', retryAfterReturn);
  }, [micDenied, phase, reportMediaError]);

  /**
   * Выход по кнопке. Сервер уведомляем сами, не полагаясь на вебхук: так остальные увидят уход
   * мгновенно, а не через паузу.
   */
  const leave = useCallback(() => leaveVoice(queryClient), [queryClient]);

  /**
   * Локальная громкость участника. Это настройка воспроизведения у меня в браузере — на то, как
   * человека слышат остальные, она не влияет никак.
   */
  const setParticipantVolume = useCallback((identity: string, volume: number) => {
    const participant = roomRef.current?.getParticipantByIdentity(identity);
    if (participant && 'setVolume' in participant) {
      (participant as { setVolume: (value: number) => void }).setVolume(volume);
    }
    useVoiceStore.getState().setLocalVolume(identity, volume);
  }, []);

  /**
   * «Не слышать этого человека». Работает через `muted` у самого элемента, а не через громкость:
   * на телефоне громкость медиа принадлежит системе (на iOS `volume` вообще только для чтения), и
   * прежний мьют «громкостью в ноль» там молча ничего не делал.
   */
  const setParticipantMuted = useCallback((identity: string, muted: boolean) => {
    const element = audioElementsRef.current.get(identity);
    if (element) {
      element.muted = muted;
    }
    useVoiceStore.getState().setLocalMuted(identity, muted);
  }, []);

  /** Списки устройств и переключение — там, где браузер реально даёт их менять. */
  const listDevices = useCallback(async (kind: 'audioinput' | 'audiooutput') => {
    try {
      return await Room.getLocalDevices(kind);
    } catch {
      return [] as MediaDeviceInfo[];
    }
  }, []);

  const switchDevice = useCallback(
    async (kind: 'audioinput' | 'audiooutput', deviceId: string): Promise<void | boolean> => {
      const activeRoom = roomRef.current;
      if (!activeRoom) return false;
      try {
        if ((await activeRoom.switchActiveDevice(kind, deviceId)) === false) return false;
      } catch (error) {
        reportMediaError(error, `device-switch-${kind}`, activeRoom);
        return false;
      }
      // Выбор, сделанный посреди разговора, запоминается на этом устройстве: следующий разговор и
      // раздел проверки обязаны показывать то же самое, иначе человек выбирает микрофон заново
      // каждый раз - ровно то, из-за чего выбор и стали запоминать.
      if (activeRoom !== roomRef.current) return false;
      const capture =
        activeRoom.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
          ?.mediaStreamTrack ?? null;
      setMicrophoneTrack(capture);
      refreshDeviceIds();
      if (
        kind === 'audioinput' &&
        IS_IOS_AUDIO_ROUTED &&
        !envConfig.IS_NATIVE &&
        !microphoneSwitchConfirmed(capture, deviceId)
      )
        return false;
      saveDevicePreference(kind === 'audioinput' ? 'mic' : 'speaker', deviceId);
      return true;
    },
    [refreshDeviceIds, reportMediaError]
  );

  /**
   * Попросить доступ к микрофону по нажатию на кнопку с меткой «доступа нет».
   *
   * <p>Нажатие в этом состоянии не должно включать микрофон вслепую: без разрешения включение
   * молча не срабатывает, и кнопка снова загорается зелёным при мёртвом входе. Вместо этого
   * спрашиваем доступ — в вебе это тот самый попап браузера — и только по факту успеха меняем
   * состояние.
   *
   * <p>Вне комнаты просим напрямую у устройства: до входа комнаты ещё нет, а метка на кнопке
   * есть, и нажатие на неё обязано работать.
   *
   * @returns удалось ли получить доступ; вызывающий по этому признаку решает, открывать ли
   *     следом список устройств
   */
  const requestMicAccess = useCallback(async (): Promise<boolean> => {
    const activeRoom = roomRef.current;
    try {
      const store = useVoiceStore.getState();
      if (activeRoom && activeRoom.state === ConnectionState.Connected) {
        await activeRoom.localParticipant.setMicrophoneEnabled(true);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Поток нужен был ровно на то, чтобы спросить разрешение: оставленный включённым, он
        // зажигает индикатор записи у человека, который ещё никуда не вошёл.
        stream.getTracks().forEach((track) => track.stop());
      }
      store.setMicDenied(false);
      store.setMicEnabled(true);
      return true;
    } catch (error) {
      reportMediaError(error, 'microphone-retry', activeRoom, true);
      useVoiceStore.getState().setMicDenied(true);
      return false;
    }
  }, [reportMediaError]);

  /**
   * Включить звук по прямому нажатию. Именно нажатие и нужно: Safari разрешает воспроизведение
   * только из пользовательского жеста, и отклонённое здесь обещание — это не сбой приложения, а
   * «человек нажал не туда». Флаг снимет `AudioPlaybackStatusChanged`, когда звук пойдёт.
   */
  const unblockAudio = useCallback(async () => {
    const activeRoom = roomRef.current;
    try {
      await activeRoom?.startAudio();
      setAudioBlocked(false);
    } catch (error) {
      reportMediaError(error, 'playback-resume', activeRoom);
      // Остаётся заблокированным — кнопка не исчезает и попробует ещё раз.
    }
  }, [reportMediaError]);

  return {
    /** Контейнер для скрытых <audio>-элементов — должен быть в дереве, пока идёт разговор. */
    audioContainerRef,
    micLevel,
    microphoneTrack,
    micDenied,
    requestMicAccess,
    audioBlocked,
    unblockAudio,
    leave,
    setParticipantVolume,
    setParticipantMuted,
    listDevices,
    switchDevice,
    activeDeviceIds,
    refreshDeviceIds,
  };
};

export const useVoiceRoom = () => {
  const mode = useNativeVoiceSession((state) => state.mode);
  const native = useNativeVoiceRoom();
  const web = useWebVoiceRoom(mode === 'web');
  return mode === 'web' ? web : native;
};

/** Собирает состав комнаты для стора из серверного ответа. */
/** Две identity LiveKit одного человека: `<userId>#<хвост>`, человек – до решётки. */
export const sameUser = (a: string, b: string) => a.split('#')[0] === b.split('#')[0];

export const toVoiceMembers = (
  participants: { userId: string; connected: boolean; host: boolean }[]
): VoiceMember[] =>
  participants.map((participant) => ({
    userId: participant.userId,
    connected: participant.connected,
    host: participant.host,
    speaking: false,
    muted: false,
  }));
